/**
 * useGameState Hook
 *
 * Manages game state, timing, scores, and moves for mfersbeheaded game.
 * Extracted from Board.js as part of refactoring effort.
 */

import React, { useState, useCallback } from 'react';
import CONSTANTS from '../constants';
import { StateChange } from '../../../types/games/mfersbeheaded';

interface GameConfig {
  LEVEL_NAME: string;
  VERSION?: string;
  TIME_LIMIT: number | null;
  MAX_MOVES: number | null;
  // Allow additional properties from level config
  [key: string]: any;
}

interface ScoreChange {
  score: number;
  t: number;
  reason: string;
}

interface BoardStateChange {
  t: number;
  changes: StateChange[];
}

interface GameStats {
  totalScore: number;
  powerUps: number;
  movesUsed: number;
  scoreChanges: ScoreChange[];
  boardStateChanges: BoardStateChange[];
  level: string;
  level_version: string;
}

interface UseGameStateReturn {
  // State
  gameState: number;
  startTime: number | null;
  endTime: number | null;
  gameStats: GameStats;

  // Computed
  elapsedTimeSince: () => number;
  isGameInProgress: () => boolean;
  isGameComplete: () => boolean;
  isGameNotYetBegun: () => boolean;

  // Actions
  startGame: () => void;
  endGame: () => void;
  updateScore: (points: number, reason?: string) => void;
  incrementMoves: () => number;
  incrementPowerUps: () => void;
  updateScoreAndMoves: (points: number, reason?: string) => { newScore: number; newMoves: number };
  updateScoreForPowerUp: (points: number) => number;

  // For direct state updates (use sparingly)
  setGameState: React.Dispatch<React.SetStateAction<number>>;
  setGameStats: React.Dispatch<React.SetStateAction<GameStats>>;
}

export const useGameState = (
  config: GameConfig,
  initStateChanges: StateChange[] = []
): UseGameStateReturn => {
  const { LEVEL_NAME, VERSION = "1.0", TIME_LIMIT, MAX_MOVES } = config;

  // Game state
  const [gameState, setGameState] = useState<number>(CONSTANTS.GAME_STATE_NOT_YET_BEGUN);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [endTime, setEndTime] = useState<number | null>(null);

  // Game statistics
  const initGameStats: GameStats = {
    totalScore: 0,
    powerUps: 0,
    movesUsed: 0,
    scoreChanges: [],
    boardStateChanges: [{ t: 0, changes: initStateChanges }],
    level: LEVEL_NAME,
    level_version: VERSION,
  };

  const [gameStats, setGameStats] = useState<GameStats>(initGameStats);

  /**
   * Calculate elapsed time since game start
   * @returns {number} Milliseconds elapsed, or 1 if game hasn't started
   */
  const elapsedTimeSince = useCallback((): number => {
    if (startTime === null) {
      return 1; // very small time but still above 0
    }
    return new Date().getTime() - startTime;
  }, [startTime]);

  /**
   * Start the game
   * Transitions from NOT_YET_BEGUN to IN_PROGRESS
   */
  const startGame = useCallback((): void => {
    if (gameState !== CONSTANTS.GAME_STATE_NOT_YET_BEGUN) {
      return; // Already started
    }

    const now = new Date().getTime();
    setStartTime(now);

    if (TIME_LIMIT != null) {
      setEndTime(now + (TIME_LIMIT * 1000));
    }

    setGameState(CONSTANTS.GAME_STATE_IN_PROGRESS);
  }, [gameState, TIME_LIMIT]);

  /**
   * End the game
   * Transitions to GAME_STATE_COMPLETE
   */
  const endGame = useCallback((): void => {
    setGameState(CONSTANTS.GAME_STATE_COMPLETE);
  }, []);

  /**
   * Update the score
   * @param {number} points - Points to add
   * @param {string} reason - Reason for score update ('match', 'power_up', etc.)
   */
  const updateScore = useCallback((points: number, reason: string = 'match'): void => {
    setGameStats(prevStats => {
      const newScore = prevStats.totalScore + points;
      return {
        ...prevStats,
        totalScore: newScore,
        scoreChanges: [
          ...prevStats.scoreChanges,
          {
            score: newScore,
            t: elapsedTimeSince(),
            reason,
          },
        ],
      };
    });
  }, [elapsedTimeSince]);

  /**
   * Increment the move counter
   * @returns {number} New move count
   */
  const incrementMoves = useCallback((): number => {
    let newMovesUsed = 0;
    let shouldEndGame = false;

    setGameStats(prevStats => {
      newMovesUsed = prevStats.movesUsed + 1;

      // Check if max moves reached
      if (MAX_MOVES !== null && newMovesUsed >= MAX_MOVES) {
        shouldEndGame = true;
      }

      return {
        ...prevStats,
        movesUsed: newMovesUsed,
      };
    });

    // End game synchronously if max moves reached
    if (shouldEndGame) {
      endGame();
    }

    return newMovesUsed;
  }, [MAX_MOVES, endGame]);

  /**
   * Increment power-up counter
   */
  const incrementPowerUps = useCallback((): void => {
    setGameStats(prevStats => ({
      ...prevStats,
      powerUps: prevStats.powerUps + 1,
    }));
  }, []);

  /**
   * Update score and moves together (common operation)
   * @param {number} points - Points to add
   * @param {string} reason - Reason for score update
   * @returns {Object} { newScore, newMoves }
   */
  const updateScoreAndMoves = useCallback((
    points: number,
    reason: string = 'match'
  ): { newScore: number; newMoves: number } => {
    // Calculate new values synchronously BEFORE setState
    // Access current state via ref to get immediate value
    const currentScore = gameStats.totalScore;
    const currentMoves = gameStats.movesUsed;

    const newScore = currentScore + points;
    const newMoves = currentMoves + 1;

    let shouldEndGame = false;

    setGameStats(prevStats => {
      // Check if max moves reached
      if (MAX_MOVES !== null && newMoves >= MAX_MOVES) {
        shouldEndGame = true;
      }

      return {
        ...prevStats,
        totalScore: newScore,
        movesUsed: newMoves,
        scoreChanges: [
          ...prevStats.scoreChanges,
          {
            score: newScore,
            t: elapsedTimeSince(),
            reason,
          },
        ],
      };
    });

    // End game synchronously if max moves reached
    if (shouldEndGame) {
      endGame();
    }

    // Return the calculated values
    return { newScore, newMoves };
  }, [MAX_MOVES, endGame, elapsedTimeSince, gameStats.totalScore, gameStats.movesUsed]);

  /**
   * Update score for power-up activation
   * @param {number} points - Points from power-up
   * @returns {number} newScore - The new total score after adding points
   */
  const updateScoreForPowerUp = useCallback((points: number): number => {
    // Calculate new score synchronously BEFORE setState
    const currentScore = gameStats.totalScore;
    const newScore = currentScore + points;

    setGameStats(prevStats => {
      return {
        ...prevStats,
        totalScore: newScore,
        powerUps: prevStats.powerUps + 1,
        scoreChanges: [
          ...prevStats.scoreChanges,
          {
            score: newScore,
            t: elapsedTimeSince(),
            reason: 'power_up',
          },
        ],
      };
    });

    return newScore;
  }, [elapsedTimeSince, gameStats.totalScore]);

  /**
   * Check if game is in progress
   * @returns {boolean}
   */
  const isGameInProgress = useCallback((): boolean => {
    return gameState === CONSTANTS.GAME_STATE_IN_PROGRESS;
  }, [gameState]);

  /**
   * Check if game is complete
   * @returns {boolean}
   */
  const isGameComplete = useCallback((): boolean => {
    return gameState === CONSTANTS.GAME_STATE_COMPLETE;
  }, [gameState]);

  /**
   * Check if game has not yet begun
   * @returns {boolean}
   */
  const isGameNotYetBegun = useCallback((): boolean => {
    return gameState === CONSTANTS.GAME_STATE_NOT_YET_BEGUN;
  }, [gameState]);

  return {
    // State
    gameState,
    startTime,
    endTime,
    gameStats,

    // Computed
    elapsedTimeSince,
    isGameInProgress,
    isGameComplete,
    isGameNotYetBegun,

    // Actions
    startGame,
    endGame,
    updateScore,
    incrementMoves,
    incrementPowerUps,
    updateScoreAndMoves,
    updateScoreForPowerUp,

    // For direct state updates (use sparingly)
    setGameState,
    setGameStats,
  };
};

export default useGameState;
